/**
 * Best practice validation for Power Automate flows
 *
 * Checks for common issues and anti-patterns:
 * - Missing error handling
 * - No retry policies on HTTP actions
 * - Loops without concurrency limits
 * - Hardcoded values instead of parameters
 * - Missing trigger conditions on high-volume triggers
 * - Direct/embedded connections instead of connection references (solution ALM)
 */

import type { FlowDefinition, ConnectionReference } from "../api/types.js";

export interface BestPracticeIssue {
  severity: "warning" | "suggestion";
  category: string;
  message: string;
  action?: string;
  fix: string;
}

export interface BestPracticeResult {
  score: number; // 0-100
  issues: BestPracticeIssue[];
  passed: string[];
}

/**
 * Check flow for best practice violations
 */
export function checkBestPractices(
  definition: FlowDefinition,
  connectionReferences?: Record<string, ConnectionReference>,
): BestPracticeResult {
  const issues: BestPracticeIssue[] = [];
  const passed: string[] = [];
  const definitionStr = JSON.stringify(definition);

  // ============================================================================
  // ERROR HANDLING CHECKS
  // ============================================================================

  // Check for error handling (runAfter with Failed status)
  const hasErrorHandling = definitionStr.includes('"Failed"') &&
                          definitionStr.includes('"runAfter"');
  const hasScope = definitionStr.includes('"type":"Scope"') ||
                   definitionStr.includes('"type": "Scope"');

  if (!hasErrorHandling && !hasScope) {
    issues.push({
      severity: "warning",
      category: "Error Handling",
      message: "No error handling detected",
      fix: "Add a Try-Catch pattern using Scope actions. Create a 'Try' scope for main logic and a 'Catch' scope with runAfter: { 'Try': ['Failed'] } for error handling.",
    });
  } else {
    passed.push("Error handling present");
  }

  // ============================================================================
  // HTTP ACTION CHECKS
  // ============================================================================

  const actions = definition.actions || {};
  const actionEntries = Object.entries(actions);

  // Check HTTP actions for retry policies and timeouts
  for (const [name, action] of actionEntries) {
    const actionObj = action as unknown as Record<string, unknown>;
    const type = actionObj.type as string;

    if (type === "Http" || type === "ApiConnection") {
      const inputs = actionObj.inputs as Record<string, unknown> | undefined;
      // retryPolicy lives inside inputs, not at the action root
      const retryPolicy = (inputs?.retryPolicy ?? actionObj.retryPolicy) as Record<string, unknown> | undefined;

      if (!retryPolicy || retryPolicy.type === "none") {
        issues.push({
          severity: "suggestion",
          category: "Resilience",
          message: `HTTP action "${name}" has no retry policy`,
          action: name,
          fix: "Add a retry policy: { \"type\": \"exponential\", \"count\": 3, \"interval\": \"PT10S\", \"maximumInterval\": \"PT1H\" }",
        });
      }

      // Check for hardcoded URLs
      if (inputs?.uri && typeof inputs.uri === "string") {
        if (!inputs.uri.startsWith("@") && !inputs.uri.includes("parameters(")) {
          issues.push({
            severity: "suggestion",
            category: "Maintainability",
            message: `HTTP action "${name}" has hardcoded URL`,
            action: name,
            fix: "Use a parameter or variable for the URL to make it configurable: @{parameters('apiUrl')}",
          });
        }
      }
    }
  }

  // ============================================================================
  // LOOP CHECKS
  // ============================================================================

  for (const [name, action] of actionEntries) {
    const actionObj = action as unknown as Record<string, unknown>;
    const type = actionObj.type as string;

    if (type === "Foreach" || type === "Until") {
      const operationOptions = actionObj.operationOptions as Record<string, unknown> | undefined;
      const runtimeConfig = actionObj.runtimeConfiguration as Record<string, unknown> | undefined;
      const concurrency = runtimeConfig?.concurrency as Record<string, number> | undefined;

      // Check for unlimited concurrency
      if (!concurrency?.repetitions && !operationOptions?.DisableAsyncPattern) {
        if (type === "Foreach") {
          issues.push({
            severity: "suggestion",
            category: "Performance",
            message: `Loop "${name}" has no concurrency limit`,
            action: name,
            fix: "Add concurrency control: { \"runtimeConfiguration\": { \"concurrency\": { \"repetitions\": 20 } } }",
          });
        }
      } else {
        passed.push(`Loop "${name}" has concurrency control`);
      }
    }
  }

  // ============================================================================
  // TRIGGER CHECKS
  // ============================================================================

  const triggers = definition.triggers || {};
  for (const [name, trigger] of Object.entries(triggers)) {
    const triggerObj = trigger as unknown as Record<string, unknown>;
    const type = triggerObj.type as string;

    // Check high-volume triggers for conditions
    if (type === "OpenApiConnectionNotification" || type === "ApiConnectionNotification") {
      const conditions = triggerObj.conditions as unknown[] | undefined;

      if (!conditions || conditions.length === 0) {
        issues.push({
          severity: "suggestion",
          category: "Performance",
          message: `Trigger "${name}" has no filter conditions`,
          action: name,
          fix: "Add trigger conditions to reduce unnecessary flow runs. Use conditions array: [{ \"expression\": \"@equals(triggerBody()?['status'], 'active')\" }]",
        });
      }
    }

    // Check Recurrence triggers for appropriate intervals
    if (type === "Recurrence") {
      const recurrence = triggerObj.recurrence as Record<string, unknown> | undefined;
      if (recurrence) {
        const frequency = recurrence.frequency as string;
        const interval = recurrence.interval as number;

        if (frequency === "Second" || (frequency === "Minute" && interval < 5)) {
          issues.push({
            severity: "warning",
            category: "Resource Usage",
            message: `Trigger "${name}" runs very frequently (${interval} ${frequency})`,
            action: name,
            fix: "Consider if this frequency is necessary. Frequent runs consume more API calls and may hit throttling limits.",
          });
        }
      }
    }
  }

  // ============================================================================
  // HARDCODED VALUES CHECK
  // ============================================================================

  // Check for hardcoded emails in SendEmail actions
  for (const [name, action] of actionEntries) {
    const actionObj = action as unknown as Record<string, unknown>;
    const inputs = actionObj.inputs as Record<string, unknown> | undefined;

    if (inputs?.parameters) {
      const params = inputs.parameters as Record<string, unknown>;
      const to = params["emailMessage/To"] as string | undefined;

      if (to && !to.startsWith("@") && to.includes("@") && to.includes(".")) {
        issues.push({
          severity: "suggestion",
          category: "Maintainability",
          message: `Action "${name}" has hardcoded email address`,
          action: name,
          fix: "Use a parameter, variable, or dynamic content for email addresses to make them configurable.",
        });
      }
    }
  }

  // ============================================================================
  // ACTION NAMING CHECK
  // ============================================================================

  // Check for default action names
  const defaultNamePatterns = [
    /^Send_an_email$/i,
    /^Get_items$/i,
    /^Update_item$/i,
    /^Condition$/i,
    /^Apply_to_each$/i,
    /^HTTP$/i,
    /^Compose$/i,
  ];

  let defaultNamesCount = 0;
  for (const name of Object.keys(actions)) {
    if (defaultNamePatterns.some(p => p.test(name))) {
      defaultNamesCount++;
    }
  }

  if (defaultNamesCount > 2) {
    issues.push({
      severity: "suggestion",
      category: "Maintainability",
      message: `${defaultNamesCount} actions have default names`,
      fix: "Rename actions to be descriptive: 'Send_approval_notification' instead of 'Send_an_email'",
    });
  }

  // ============================================================================
  // SOLUTION / ALM CHECKS
  // ============================================================================

  // Flows in a solution should use connection references (not embedded/direct
  // connections) so connections can be provided at import time. Embedded
  // connections are pinned to a specific environment's connection and break
  // clean solution deployment. Signal: connectionReference.source === "Embedded".
  if (connectionReferences && Object.keys(connectionReferences).length > 0) {
    const embedded = Object.entries(connectionReferences).filter(
      ([, ref]) => ref?.source === "Embedded",
    );
    if (embedded.length > 0) {
      const names = embedded.map(([k]) => k).join(", ");
      issues.push({
        severity: "warning",
        category: "Solution / ALM",
        message: `${embedded.length} connection${embedded.length > 1 ? "s use" : " uses"} an embedded (direct) connection instead of a connection reference: ${names}`,
        fix: "Flows in a solution should use connection references so connections can be provided during import. Edit the flow inside the solution and switch each connection to a connection reference (source 'Invoker'), which simplifies application lifecycle management across environments.",
      });
    } else {
      passed.push("Uses connection references (solution ALM friendly)");
    }
  }

  // ============================================================================
  // CALCULATE SCORE
  // ============================================================================

  // Start with 100 and deduct points
  let score = 100;

  for (const issue of issues) {
    if (issue.severity === "warning") {
      score -= 15;
    } else {
      score -= 5;
    }
  }

  score = Math.max(0, Math.min(100, score));

  return { score, issues, passed };
}

/**
 * Format best practice results for display
 */
export function formatBestPracticeResults(result: BestPracticeResult): string {
  let output = `## Best Practice Analysis\n\n`;
  output += `**Score:** ${result.score}/100\n\n`;

  if (result.passed.length > 0) {
    output += `### ✅ Passed\n`;
    for (const p of result.passed) {
      output += `  • ${p}\n`;
    }
    output += `\n`;
  }

  if (result.issues.length > 0) {
    const warnings = result.issues.filter(i => i.severity === "warning");
    const suggestions = result.issues.filter(i => i.severity === "suggestion");

    if (warnings.length > 0) {
      output += `### ⚠️ Warnings\n`;
      for (const issue of warnings) {
        output += `  • **${issue.category}:** ${issue.message}\n`;
        output += `    Fix: ${issue.fix}\n\n`;
      }
    }

    if (suggestions.length > 0) {
      output += `### 💡 Suggestions\n`;
      for (const issue of suggestions) {
        output += `  • **${issue.category}:** ${issue.message}\n`;
        output += `    Fix: ${issue.fix}\n\n`;
      }
    }
  } else {
    output += `### 🎉 No issues found!\n`;
    output += `This flow follows Power Automate best practices.\n`;
  }

  return output;
}
